import { Component, Input, OnInit } from '@angular/core';
import { BaseViewFieldType } from '../dynamic-view-v2/enums/BaseViewFieldType';
import { DateTime } from 'luxon';
import { UtilitiesService } from '../../services/utilities.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-base-view-field',
  templateUrl: './base-view-field.component.html',
  styleUrls: ['./base-view-field.component.sass']
})
export class BaseViewFieldComponent implements OnInit {
  @Input() viewTypes: BaseViewFieldType[];
  @Input() field: any;
  @Input() record: any;
  @Input() options: any;
  @Input() keysToRender: Array<string> = [];

  checkTimezone: boolean = false;
  isExpanded: boolean = false;
  abbreviatedText: string = '';
  fullText: string = '';

  BaseViewFieldType = BaseViewFieldType;

  formatTimeTaken(value: any): string {
    if (value !== null && value !== undefined) {
      const timeTaken = parseFloat(value);
      const minutes = Math.floor(timeTaken);
      const seconds = Math.round((timeTaken - minutes) * 60);
      return `${minutes}m ${seconds}s`;
    }
    return '';
  }
  
  getLabelForValue(value: number): string {
    const option = this.field.options?.values?.find((o: any) => o.value === value);
    return option ? option.name : 'Unknown';
  }

  getNestedValue(obj: any, path: string): any {
    // Special handling for stageMilestone in EO tracks (type 6)
    if (path === 'stageMilestone' && obj?.type === 6) {
      return ''; // Return empty string for EO tracks
    }
    
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }
  
  get keys() {
    if (this.record) {
      // If keysToRender has elements, filter the keys; otherwise, return all keys
      return this.keysToRender?.length > 0
        ? Object.keys(this.record).filter(key => this.keysToRender.includes(key))
        : Object.keys(this.record);
    } else {
      return [];
    }
  }

  isObject(value: any): boolean {
    return typeof value === 'object' && !Array.isArray(value) && value !== null;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }
  
  isString(value: any): boolean {
    return typeof value === 'string';
  }
  
  isLink(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const regex = /^https?:\/\/[^ "]+$/;
    return regex.test(value);
  }

  camelCaseToWords(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1') // Insert a space before each capital letter
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
  }

  /**
  * Returns the label for a dropdown field.
  * @param record The record containing the field.
  * @param field The field to get the label for.
  * @returns The label for the dropdown field.
 **/
  
  getDropdownLabel(record: any, field:any) {
    if (record && record[field]) {
      if (field.options) {
        console.group('field', field.options);
      }
      return `${record[field].name || record[field].description || record[field].title} (${record[field].id})`;
    }
    return null;
  }

  constructor(
    private utilitiesService: UtilitiesService,
    private sanitizer: DomSanitizer,
  ) {
  }

  ngOnInit() {
  this.checkTimezone = this.utilitiesService.isUserLocalTimeZonePST();
  
  // Handle abbreviated metadata
  if (this.field.isAbbreviatedMetadata) {
    this.processAbbreviatedMetadata();
  }
  }

  localTime(date: any) {
    const localDate = DateTime.fromJSDate(new Date(date)).toLocal();
    return `${localDate.toFormat('MMM d, yyyy, hh:mm a')} ${localDate.offsetNameShort}`;
  }

  getBooleanLabel(value: boolean, options: { true: string; false: string }): string {
    return value ? options.true : options.false;
  }

  /**
   * Converts URLs in text to clickable links that open in a new tab
   * @param text The text containing URLs to convert
   * @returns The text with URLs converted to clickable links
   */
  convertUrlsToLinks(text: string): string {
    if (!text) return '';
    
    // This is a more robust approach that works with complex HTML
    // We'll use a regex-based solution that can handle HTML tags
    
    // Split the HTML into tokens (tags and text)
    const tokenRegex = /(<\/?[^>]+>)|([^<]+)/g;
    const tokens = text.match(tokenRegex) || [];
    
    // Process each token
    const processedTokens = tokens.map(token => {
      // If this is an HTML tag, leave it as is
      if (token.startsWith('<') && token.endsWith('>')) {
        // Skip processing if it's an anchor tag or contains href/src attributes
        if (token.match(/<a\s|<\/a>|href=|src=/i)) {
          return token;
        }
        return token;
      }
      
      // Otherwise, convert URLs to links
      // Simple but effective regex that captures most URLs including those with query params and fragments
      const urlRegex = /(https?:\/\/[^\s<>"'\)]+)/g;
      return token.replace(urlRegex, (url) => {
        // Clean the URL to ensure it's properly formatted
        const cleanUrl = url.trim();
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" style="color: var(--primary-color); cursor: pointer; text-decoration: underline;">${cleanUrl}</a>`;
      });
    });
    
    // Join the tokens back together
    return processedTokens.join('');
  }
  
  getSafeHtml(html: string): SafeHtml {
    // First convert any URLs in the HTML to clickable links
    const htmlWithLinks = this.convertUrlsToLinks(html);
    // Ensure existing anchor tags have proper attributes for external links
    const htmlWithProperLinks = this.ensureProperLinkAttributes(htmlWithLinks);
    // Then sanitize the HTML
    return this.sanitizer.bypassSecurityTrustHtml(htmlWithProperLinks);
  }

  /**
   * Ensures that existing anchor tags have proper attributes for external links
   * @param html The HTML string to process
   * @returns HTML with properly configured anchor tags
   */
  ensureProperLinkAttributes(html: string): string {
    if (!html) return '';
    
    // Find anchor tags that don't already have target="_blank" and add it
    return html.replace(/<a\s+([^>]*?)href\s*=\s*["']([^"']+)["']([^>]*?)>/gi, (match, beforeHref, url, afterHref) => {
      // Check if it's an external URL (starts with http:// or https://)
      if (url.match(/^https?:\/\//)) {
        // Check if target attribute is already present
        if (!match.match(/target\s*=/i)) {
          // Add target="_blank" and rel attributes for external links
          return `<a ${beforeHref}href="${url}"${afterHref} target="_blank" rel="noopener noreferrer" style="color: var(--primary-color); cursor: pointer; text-decoration: underline;">`;
        }
      }
      return match;
    });
  }

  isValidRichText(value: any): boolean {
    return value !== null && value !== undefined && value.trim() !== '';
  }

  /**
   * Handles mousedown events on rich text content to properly open links
   * @param event The mousedown event
   */
  handleRichTextClick(event: Event): boolean | void {
    const mouseEvent = event as MouseEvent;
    if (mouseEvent.button !== 0) return; // Only handle left clicks
    
    const target = event.target as HTMLElement;
    let anchorElement: HTMLAnchorElement | null = null;
    
    if (target.tagName === 'A') {
      anchorElement = target as HTMLAnchorElement;
    } else {
      anchorElement = target.closest('a');
    }
    
    if (anchorElement && anchorElement.href) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      const originalHref = anchorElement.href;
      anchorElement.removeAttribute('href');
      
      window.open(originalHref, '_blank', 'noopener,noreferrer');
      
      setTimeout(() => {
        if (anchorElement) {
          anchorElement.setAttribute('href', originalHref);
        }
      }, 100);
      
      return false;
    }
  }

  /**
   * Gets the URL from an item, handling both string and object cases
   * @param item The item which can be a string or object with link property
   * @returns The URL with proper http prefix
   */
  getExternalLinkUrl(item: any): string {
    console.log('item', typeof item.link);
    if (!item) return '';
    
    let url: string;
    
    // Handle object with link property
    if (typeof item === 'object' && item.link) {
      // If item.link is also an object, try to get a URL property
      
        url = item.link
      }
    // Handle string or other types converted to string
    else {
      url = item.toString();
    }
    console.log('url', url);
    // Add http prefix if not present
    return url.startsWith('http') ? url : 'http://' + url;
  }

  /**
   * Gets the display text for an external link item
   * @param item The item which can be a string or object with link property
   * @returns The text to display for the link
   */
  getExternalLinkText(item: any): string {
    
    if (!item) return '';
    
    // Handle object with link property
    if (typeof item === 'object') {
      // If item.link is also an object, try to get a readable property
      return item.link;
    }
    
    // Handle string or other types
    return item.toString();
  }

  /**
   * Checks if an item has a valid external link
   * @param item The item to check
   * @returns True if the item has a valid link
   */
  hasValidExternalLink(item: any): boolean {
    if (!item || !item.link) return false;
    
    // Handle object with link property
    if (typeof item === 'object') {
      // Check if link has content (length > 0)
      const linkStr = typeof item.link === 'string' ? item.link : item.link.toString();
      return linkStr && linkStr.trim().length > 0;
    }
    
    // Handle string items
    if (typeof item === 'string') {
      return item.trim().length > 0;
    }
    
    // Handle other types that can be converted to string
    if (item && item.toString) {
      const itemStr = item.toString();
      return itemStr && itemStr.trim().length > 0;
    }
    
    return false;
  }

  private processAbbreviatedMetadata(): void {
    const value = this.getNestedValue(this.record, this.field.key);
    if (!value) {
      this.abbreviatedText = '';
      this.fullText = '';
      return;
    }

    // Convert to string if it's an object
    if (typeof value === 'object') {
      this.fullText = JSON.stringify(value, null, 2);
    } else {
      this.fullText = String(value);
    }

    const maxLength = this.field.maxLength || 100;

    // Create abbreviated version
    if (this.fullText.length <= maxLength) {
      this.abbreviatedText = this.fullText;
    } else {
      this.abbreviatedText = this.fullText.substring(0, maxLength) + '...';
    }
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  getDisplayText(): string {
    if (this.field.isAbbreviatedMetadata) {
      return this.isExpanded ? this.fullText : this.abbreviatedText;
    }
    return this.getNestedValue(this.record, this.field.key);
  }

  shouldShowToggle(): boolean {
    return this.field.isAbbreviatedMetadata && this.fullText.length > (this.field.maxLength || 100);
  }
}



